import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Form } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faDownload, faPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ToggleSwitch from 'react-switch';

import { anchovy, grape } from 'lib/css/colors';
import { CUT_DRY_CLIENT_ID } from 'lib/constants';
import EditableProperty, {
  MANUFACTURER_FIELDS,
} from 'components/manage_manufacturers/manufacturer_details/EditableProperty';
import { faSquareCheck } from 'images/icons/fsa-monotone-svg-icons';
import { InfluenceStatusOptions } from 'lib/enums/InfluenceStatus';
import { ManufacturerGlnSourceOptions } from 'lib/enums/ManufacturerGlnSource';
import pointerOnHover from 'lib/css/pointerOnHover';
import { normalizeUrl } from 'lib/utils/stringUtils';

const MANUFACTURER_TOGGLE_FIELDS = [
  MANUFACTURER_FIELDS.IS_PARTNER,
  MANUFACTURER_FIELDS.UPB_DATA_PROVIDER,
  MANUFACTURER_FIELDS.REQUIRES_MANUFACTURER_PORTAL,
  MANUFACTURER_FIELDS.PRESERVE_PRODUCT_NAME_CASING,
];

const CLIENT_GLOBAL_MANUFACTURER_TOGGLE_FIELDS = [
  MANUFACTURER_FIELDS.SYNC_BRANDED_PRODUCTS_ONLY_TO_CD,
];

function ManufacturerRow({
  label,
  title,
  manufacturer,
  updateManufacturer,
  refetch,
  clientGlobalManufacturerData,
  updateClientGlobalManufacturer,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [updatedManufacturer, setUpdatedManufacturer] = useState({ id: manufacturer.id });

  const getGlnStatusLabel = gln => {
    const status = manufacturer.manufacturer_glns_status?.[gln];
    if (!status) return null;

    switch (status) {
      case 'submitted':
        return { label: 'Submitted', color: 'text-warning' };
      case 'subscribed':
        return { label: 'Subscribed', color: 'text-success' };
      case 'success':
        return { label: 'Success', color: 'text-success' };
      case 'failed':
        return { label: 'Failed', color: 'text-danger' };
      default:
        return null;
    }
  };

  const handleClientGlobalManufacturerToggle = fieldName => {
    const currentValue = clientGlobalManufacturerData?.data?.configs?.[fieldName] || false;
    updateClientGlobalManufacturer({
      client_id: CUT_DRY_CLIENT_ID,
      global_manufacturer_id: manufacturer.id,
      configs: {
        [fieldName]: !currentValue,
      },
    });
  };

  const isManufacturerToggleField = MANUFACTURER_TOGGLE_FIELDS.includes(label);
  const isClientGlobalManufacturerToggleField =
    CLIENT_GLOBAL_MANUFACTURER_TOGGLE_FIELDS.includes(label);

  return (
    <div className={css(styles.container)}>
      <p className={css(styles.label)}>{title}</p>
      {isManufacturerToggleField ? (
        <div>
          <ToggleSwitch
            onColor={grape}
            checked={manufacturer[label]}
            checkedIcon={false}
            uncheckedIcon={false}
            onChange={() =>
              updateManufacturer({
                id: manufacturer.id,
                [label]: !manufacturer[label],
              })
            }
          />
        </div>
      ) : isClientGlobalManufacturerToggleField ? (
        <div>
          <ToggleSwitch
            onColor={grape}
            checked={clientGlobalManufacturerData?.data?.configs?.[label] || false}
            checkedIcon={false}
            uncheckedIcon={false}
            onChange={() => handleClientGlobalManufacturerToggle(label)}
          />
        </div>
      ) : isEditing ? (
        <Form
          onBlur={() => {
            if (
              ![
                MANUFACTURER_FIELDS.SIMILAR_MANUFACTURERS,
                MANUFACTURER_FIELDS.MANUFACTURER_GLNS,
              ].includes(label)
            ) {
              setIsEditing(false);
            }
          }}
          className={
            label === MANUFACTURER_FIELDS.SIMILAR_MANUFACTURERS
              ? css(styles.rowNotEditable)
              : css(styles.row)
          }
        >
          {label !== MANUFACTURER_FIELDS.SIMILAR_MANUFACTURERS ? (
            <FontAwesomeIcon
              className={classNames(pointerOnHover, css(styles.editIcon))}
              icon={faSquareCheck}
              onMouseDown={() => {
                updateManufacturer(updatedManufacturer);
                setIsEditing(false);
              }}
              size="lg"
            />
          ) : null}
          <EditableProperty
            label={label}
            manufacturer={manufacturer}
            setIsEditing={setIsEditing}
            setUpdatedManufacturer={setUpdatedManufacturer}
            refetch={refetch}
          />
        </Form>
      ) : (
        <div className={css(styles.row)}>
          <FontAwesomeIcon
            className={classNames(pointerOnHover, css(styles.editIcon))}
            icon={faPen}
            onClick={() => setIsEditing(true)}
          />
          {label === 'description' ? (
            <div>
              {manufacturer[label] ? (
                manufacturer[label]
                  .split('\n')
                  .map((paragraph, index) => <p key={index}>{paragraph}</p>)
              ) : (
                <p>-</p>
              )}
            </div>
          ) : label === MANUFACTURER_FIELDS.SIMILAR_MANUFACTURERS ? (
            <div>
              {manufacturer[label].length ? (
                manufacturer[label].map(similar_mfr => (
                  <p key={similar_mfr.id}>&bull; {similar_mfr.manufacturer_name}</p>
                ))
              ) : (
                <p>-</p>
              )}
            </div>
          ) : label === MANUFACTURER_FIELDS.MANUFACTURER_GLNS ? (
            <div>
              {manufacturer[label] && Object.keys(manufacturer[label]).length > 0 ? (
                Object.entries(manufacturer[label]).map(([gln, subCompanyName], index) => {
                  const statusLabel = getGlnStatusLabel(gln);
                  const subscriptionFileUrl = manufacturer.gdsn_subscription_files?.[gln];

                  return (
                    <div key={index} className={css(styles.glnContainer)}>
                      <p className={css(styles.glnItem)}>
                        • {subCompanyName ? `${subCompanyName}` : 'Main Company'} - {gln}
                        {statusLabel && (
                          <span className={`${css(styles.statusLabel)} ${statusLabel.color}`}>
                            ({statusLabel.label})
                          </span>
                        )}
                      </p>
                      {subscriptionFileUrl && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className={css(styles.downloadButton)}
                          href={subscriptionFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FontAwesomeIcon icon={faDownload} className={css(styles.downloadIcon)} />
                          Download XML
                        </Button>
                      )}
                    </div>
                  );
                })
              ) : (
                <p>-</p>
              )}
            </div>
          ) : label === MANUFACTURER_FIELDS.GLN_SOURCE ? (
            <p>
              {ManufacturerGlnSourceOptions.find(opt => opt.value === manufacturer[label])?.label ||
                'None (Default)'}
            </p>
          ) : label === MANUFACTURER_FIELDS.INFLUENCE_STATUS ? (
            <p>
              {InfluenceStatusOptions.find(opt => opt.value === manufacturer[label])?.label ||
                'None'}
            </p>
          ) : label === 'manufacturer_url' ? (
            manufacturer[label] ? (
              <a href={normalizeUrl(manufacturer[label])} target="_blank" rel="noopener noreferrer">
                {manufacturer[label]}
              </a>
            ) : (
              <p>-</p>
            )
          ) : (
            <p> {manufacturer[label] || '-'}</p>
          )}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  label: {
    display: 'inline',
    fontWeight: 'bold',
    color: anchovy,
  },
  row: {
    display: 'flex',
    marginLeft: '-1.65rem',
  },
  rowNotEditable: {
    display: 'flex',
  },
  editIcon: {
    marginRight: '0.75rem',
  },
  glnItem: {
    marginBottom: '0.25rem',
    display: 'inline-block',
  },
  glnContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  statusLabel: {
    fontSize: '0.9em',
  },
  downloadButton: {
    marginLeft: '1rem',
    padding: '0.25rem 0.5rem',
    fontSize: '0.875rem',
  },
  downloadIcon: {
    marginRight: '0.25rem',
  },
});

ManufacturerRow.propTypes = {
  label: PropTypes.string,
  title: PropTypes.string,
  manufacturer: PropTypes.object,
  updateManufacturer: PropTypes.func,
  refetch: PropTypes.func,
  clientGlobalManufacturerData: PropTypes.object,
  updateClientGlobalManufacturer: PropTypes.func,
};

export default ManufacturerRow;
